/**
 * NavaStyles — injeta a fonte Host Grotesk, animações e reset global Nava.
 *
 * Renderize UMA vez na raiz da aplicação (já feito em App.jsx).
 * Extraído literalmente do protótipo para preservar identidade visual.
 *
 * Acessibilidade: respeita prefers-reduced-motion desativando animações
 * navaPulse e navaBlink.
 */
import { C, getCurrentTheme, themeFontFamily } from './palette';

export function NavaStyles() {
  const sphere = getCurrentTheme() === 'sphere';
  // Sphere usa a Inter LOCAL (@font-face em index.css); os temas antigos mantêm a
  // Host Grotesk do Google Fonts (só são baixadas quando escolhidos).
  const fontImport = sphere
    ? ''
    : "@import url('https://fonts.googleapis.com/css2?family=Host+Grotesk:wght@300;400;500;600;700;800&display=swap');";
  return (
    <style>{`
      ${fontImport}

      *, *::before, *::after {
        font-family: ${themeFontFamily()};
        box-sizing: border-box;
      }

      body {
        font-feature-settings: ${sphere ? '"cv11", "ss01"' : 'normal'};
        color: ${C.neutral};
        color-scheme: dark;
      }

      /* Números alinhados em tabelas/indicadores (tema Sphere). */
      .nava-num { font-variant-numeric: tabular-nums; }

      html, body, #root {
        height: 100%;
      }

      body {
        background-color: ${C.black};
        margin: 0;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      input, textarea, select, button {
        font-family: inherit;
      }

      input:focus, textarea:focus, select:focus {
        border-color: ${C.cyber} !important;
        outline: none;
      }

      /* Foco de teclado visível — acessibilidade (WCAG 2.4.7) */
      :focus-visible {
        outline: 2px solid ${sphere ? C.cyan : C.cyber};
        outline-offset: 2px;
      }

      /* Scrollbar customizada */
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: ${C.black}; }
      ::-webkit-scrollbar-thumb { background: ${C.blackBorder}; border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: ${C.neutralMuted}; }

      /* Animações Nava */
      @keyframes navaPulse {
        0%   { box-shadow: 0 0 0 0 ${C.cyber}99; }
        100% { box-shadow: 0 0 0 8px ${C.cyber}00; }
      }

      @keyframes navaBlink {
        0%, 50%  { opacity: 1; }
        51%, 100% { opacity: 0; }
      }

      /* Entrada de novas linhas de log (Live Board) — fade + slide sutil */
      @keyframes navaLogIn {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      /* Ponto "ao vivo" pulsante (header de logs / streaming) */
      @keyframes navaLiveDot {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.35; transform: scale(0.7); }
      }

      /* Spinner de rotação (Mission Control: span em execução) */
      @keyframes navaSpin {
        from { transform: rotate(0deg); }
        to   { transform: rotate(360deg); }
      }

      /* Mission Control — topologia MAIOR: o SVG do mermaid preenche a largura */
      .nava-topology { min-height: 520px; }
      .nava-topology svg { width: 100% !important; height: auto !important; min-height: 480px; }

      /* AgentRoom — personagem entra em cena (subagente aparece dinamicamente) */
      @keyframes navaAgentIn {
        from { opacity: 0; transform: translateY(10px) scale(0.9); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
      /* AgentRoom — anel pulsante do agente ativo */
      @keyframes navaAgentRing {
        0%   { box-shadow: 0 0 0 0 var(--nava-ring, rgba(1,230,228,0.5)); }
        70%  { box-shadow: 0 0 0 10px rgba(0,0,0,0); }
        100% { box-shadow: 0 0 0 0 rgba(0,0,0,0); }
      }
      /* AgentRoom — pontinhos de "digitando/pensando" */
      @keyframes navaDot {
        0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
        40%           { opacity: 1;   transform: translateY(-3px); }
      }
      /* AgentRoom — leve "respiração" do avatar trabalhando */
      @keyframes navaAgentWork {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-2px); }
      }

      /* Splash (abertura) — entrada da marca e barra de auto-avanço */
      @keyframes navaFadeUp {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      @keyframes navaSplashBar {
        from { transform: scaleX(0); }
        to   { transform: scaleX(1); }
      }

      /* Logo animado (sidebar) — glow "respirando" + brilho que varre a marca,
         evocando a abertura pré-login. */
      @keyframes navaLogoGlow {
        0%, 100% { opacity: 0.45; transform: scale(0.98); }
        50%      { opacity: 0.9;  transform: scale(1.04); }
      }

      @keyframes navaShine {
        0%   { background-position: 180% 0; }
        60%  { background-position: -60% 0; }
        100% { background-position: -60% 0; }
      }

      /* Barra de progresso INDETERMINADA — usada quando ainda não há % real
         (fases de clone/leitura/grafo), pra deixar claro que está trabalhando. */
      @keyframes navaIndeterminate {
        0%   { left: -40%; width: 40%; }
        50%  { left: 30%;  width: 55%; }
        100% { left: 100%; width: 40%; }
      }

      /* Respeita preferência do sistema — desativa animações */
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }
    `}</style>
  );
}
